/**
 * Sanitization for notification title/body HTML (DOMPurify + denylisted inline CSS / attrs).
 * Style and dimensional attributes are cleaned inside DOMPurify via `uponSanitizeAttribute`.
 */
import DOMPurify from 'dompurify';

const NOTIFICATION_ALLOWED_TAGS = ['a', 'br', 'strong', 'em', 'b', 'i', 'u', 'span'];
/** `width` / `height` are listed so the hook can strip trap values; `style` is cleaned by the same hook. */
const NOTIFICATION_ALLOWED_ATTR = ['href', 'target', 'rel', 'aria-label', 'style', 'width', 'height'];

/** Collapse vendor prefixes so `-webkit-opacity` is treated as `opacity`. */
const canonicalCssProperty = (prop: string): string => {
  let p = prop.trim().toLowerCase();
  while (/^-([a-z]+)-/i.test(p)) {
    p = p.replace(/^-[a-z]+-/, '');
  }
  return p;
};

const isOverlayLikeBackground = (value: string): boolean => {
  const v = value.trim();
  if (!v) return false;
  if (/^(white|#fff\b|#ffffff\b)/i.test(v)) return true;
  if (/^rgba?\(\s*255\s*,\s*255\s*,\s*255\b/i.test(v)) return true;
  if (/^rgba?\(\s*0\s*,\s*0\s*,\s*0\s*,\s*(0?\.\d+|0)\s*\)/i.test(v)) return true;
  if (/^transparent$/i.test(v)) return true;
  return false;
};

export const isFullViewportWidthOrHeight = (value: string): boolean => {
  const compact = value.replace(/\s+/g, '').toLowerCase();
  return /^100%$/.test(compact) || /100vh/i.test(compact) || /100vw/i.test(compact);
};

/**
 * Removes denylisted CSS declarations from an inline `style` attribute value.
 */
export const stripDeniedCssProperties = (styleValue: string): string | null => {
  if (!styleValue || !styleValue.trim()) return null;

  const originalLower = styleValue.toLowerCase();
  const hadPositioning = /position\s*:\s*(fixed|absolute|sticky)/i.test(originalLower);

  const declarations = styleValue.split(';').map((s) => s.trim()).filter(Boolean);
  const kept: string[] = [];

  for (const decl of declarations) {
    const colon = decl.indexOf(':');
    if (colon === -1) continue;
    const rawProp = decl.slice(0, colon).trim();
    const prop = canonicalCssProperty(rawProp);
    const value = decl.slice(colon + 1).trim();
    const valueLower = value.toLowerCase();

    if (prop === 'position' && /(fixed|absolute|sticky)/i.test(value)) continue;
    if (prop === 'z-index') continue;
    if ((prop === 'width' || prop === 'height') && isFullViewportWidthOrHeight(value)) continue;
    if (prop === 'top' || prop === 'left' || prop === 'right' || prop === 'bottom') continue;
    if ((prop === 'overflow' || prop === 'overflow-x' || prop === 'overflow-y') && valueLower.includes('hidden')) {
      continue;
    }
    if (prop === 'pointer-events') continue;
    if ((prop === 'background' || prop === 'background-color') && isOverlayLikeBackground(value)) continue;
    if (prop === 'opacity' && hadPositioning) continue;

    kept.push(`${rawProp}: ${value}`);
  }

  if (kept.length === 0) return null;
  return kept.join('; ');
};

type UponSanitizeAttributeData = {
  attrName?: string;
  attrValue?: string;
  keepAttr?: boolean;
};

/** Stable top-level reference (avoids re-creating a closure on every sanitize call). */
function notificationUponSanitizeAttributeHook(_node: unknown, data: UponSanitizeAttributeData): void {
  const name = (data.attrName || '').toLowerCase();
  if (name === 'style' && typeof data.attrValue === 'string') {
    const cleaned = stripDeniedCssProperties(data.attrValue);
    if (cleaned == null || !cleaned.trim()) {
      data.keepAttr = false;
    } else {
      data.attrValue = cleaned;
    }
    return;
  }
  if ((name === 'width' || name === 'height') && data.attrValue != null && isFullViewportWidthOrHeight(String(data.attrValue))) {
    data.keepAttr = false;
  }
}

/** Tags and attrs not listed here are already dropped by DOMPurify (allowlist mode); FORBID_* is redundant. */
const DOM_PURIFY_NOTIFICATION_CONFIG = {
  ALLOWED_TAGS: NOTIFICATION_ALLOWED_TAGS,
  ALLOWED_ATTR: NOTIFICATION_ALLOWED_ATTR,
  ALLOW_DATA_ATTR: false,
};

/**
 * Full notification fragment sanitize (tags/attrs + denylisted CSS via DOMPurify hook).
 */
export const sanitizeNotificationHtml = (html: string): string => {
  DOMPurify.addHook('uponSanitizeAttribute', notificationUponSanitizeAttributeHook);
  try {
    return DOMPurify.sanitize(html || '', DOM_PURIFY_NOTIFICATION_CONFIG);
  } finally {
    // addHook/removeHook are paired in this try/finally and DOMPurify.sanitize is synchronous,
    // so the single-arg removeHook reliably removes the hook we just added (LIFO per entry point).
    DOMPurify.removeHook('uponSanitizeAttribute');
  }
};
